// Client-side checks for an English auction bid. Mirrors the server rules in
// backend/src/services/bidding.service.js; the server re-validates every bid.
//
// Base bid:  bidType RELATIVE_TO_LAST_BID_BY_SELF -> user's own last bid
//            otherwise                            -> lead bid (H1 / L1)
//            (start price when there is no such bid)
// Variable YES -> bid must be at least one step better than the base bid
// Variable NO  -> bid must also be on the fixed step sequence from start price
// previewPeriod MANDATORY -> a bidder's first bid on a lot (firstBidRequired)
//                            is blind: any positive amount
//
// Returns an error message, or null when the bid passes.
export const getEnglishBidError = ({ eventDetails, lotDetails, amount }) => {
  // MANDATORY preview period: the first bid is blind, any positive amount
  if (lotDetails.firstBidRequired) {
    return parseFloat(amount) > 0 ? null : "Please enter a valid bid amount.";
  }

  const isForward = eventDetails.eventType === "FORWARD_AUCTION";
  const isVariable = eventDetails.variableIncrementDecrement === "YES";
  const isPercentage = eventDetails.incrementBidAmountBy === "PERCENTAGE";
  const isOwnLastBid = eventDetails.bidType === "RELATIVE_TO_LAST_BID_BY_SELF";

  const bidValue = parseFloat(amount);
  const startPrice = parseFloat(lotDetails.startPrice);
  const increment = parseFloat(lotDetails.increment);
  const baseBid =
    parseFloat(
      isOwnLastBid ? lotDetails.myBidStatus?.myLastBid : lotDetails.highestBid
    ) || startPrice;

  const direction = isForward ? "higher" : "lower";
  const baseLabel = isOwnLastBid ? "your last bid" : "the current bid";
  const directionError = `Please place a bid ${direction} than ${baseLabel}.`;

  // Fixed percentage: bid must be a step of startPrice × (1 ± rate%)^n and beat the base bid
  if (!isVariable && isPercentage) {
    const base = isForward ? 1 + increment / 100 : 1 - increment / 100;
    if (base > 0 && startPrice > 0 && bidValue > 0) {
      const n = Math.log(bidValue / startPrice) / Math.log(base);
      const nRounded = Math.round(n);
      if (!(nRounded > 0 && Math.abs(n - nRounded) < 1e-4)) {
        return `Bid amount must be a valid step in the ${lotDetails.increment}% sequence from the start price.`;
      }
    }
    const advances = isForward ? bidValue > baseBid : bidValue < baseBid;
    return advances ? null : directionError;
  }

  // Variable, or fixed value: bid must be at least one step better than the base bid
  const step = isPercentage ? (baseBid * increment) / 100 : increment;
  const requiredBid = parseFloat(
    (isForward ? baseBid + step : baseBid - step).toFixed(2)
  );
  if (isForward ? bidValue < requiredBid : bidValue > requiredBid) {
    return directionError;
  }

  // Fixed value: bid must be an exact multiple of increment from the current bid
  // (or the start price when there is none), matching the server check
  if (!isVariable) {
    const diff = Math.round((bidValue - baseBid) * 1e6);
    const incr = Math.round(increment * 1e6);
    if (incr > 0 && diff % incr !== 0) {
      const firstExample = isForward ? baseBid + increment : baseBid - increment;
      const secondExample = isForward
        ? baseBid + 2 * increment
        : baseBid - 2 * increment;
      return `Bid amount must be in steps of ${increment} from ${baseBid} (e.g. ${firstExample}, ${secondExample}, ...).`;
    }
  }

  return null;
};
